import {
  getRefreshToken,
  getUserToken,
  updateTokenStorage,
  updateRefreshTokenStorage,
  removeRefreshTokenStorage,
  removeItem,
} from "./storage-data";
import apiRequest from "./api-request";
import { getValidateSnapshot } from "./auth/validate-cache";
import {
  getSessionPrintHouseId,
  setSessionPrintHouseId,
} from "./auth/session-print-house-id";

let isRefreshing = false;
/** Latest explicit print-house context for an in-flight or queued refresh. */
let pendingRefreshOptions: RefreshAccessTokenOptions | undefined;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

export type RefreshAccessTokenOptions = {
  /** Overrides stored session print house (e.g. right after switcher select). */
  printHouseId?: string;
  timeZoneOffset?: number;
};

const extractCustomerFromValidateSnapshot = (
  snapshot: unknown,
): { printHouseId?: string; timeZoneOffset?: number } | null => {
  if (!snapshot || typeof snapshot !== "object") {
    return null;
  }
  const root = snapshot as Record<string, unknown>;
  const data = root.data as Record<string, unknown> | undefined;
  const inner = data?.data as Record<string, unknown> | undefined;
  const customer =
    (inner?.customer as Record<string, unknown> | undefined) ??
    (data?.customer as Record<string, unknown> | undefined) ??
    (root.customer as Record<string, unknown> | undefined);
  if (!customer) {
    return null;
  }
  const printHouseId =
    typeof customer.printHouseId === "string"
      ? customer.printHouseId
      : customer.printHouseId != null
        ? String(customer.printHouseId)
        : undefined;
  const timeZoneOffset =
    typeof customer.timeZoneOffset === "number"
      ? customer.timeZoneOffset
      : undefined;
  return { printHouseId, timeZoneOffset };
};

const resolveRefreshPrintHouseContext = (
  options?: RefreshAccessTokenOptions,
): { printHouseId?: string; timeZoneOffset?: number } => {
  if (options?.printHouseId) {
    return {
      printHouseId: options.printHouseId,
      timeZoneOffset: options.timeZoneOffset,
    };
  }

  const storedPrintHouseId = getSessionPrintHouseId();
  if (storedPrintHouseId) {
    const snapshot = extractCustomerFromValidateSnapshot(getValidateSnapshot());
    return {
      printHouseId: storedPrintHouseId,
      timeZoneOffset: options?.timeZoneOffset ?? snapshot?.timeZoneOffset,
    };
  }

  return extractCustomerFromValidateSnapshot(getValidateSnapshot()) ?? {};
};

// Export to check if we're currently refreshing (used by apiRequest to prevent loops)
export const getIsRefreshing = () => isRefreshing;

export const refreshAccessToken = async (
  options?: RefreshAccessTokenOptions,
): Promise<string | null> => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  if (options?.printHouseId) {
    setSessionPrintHouseId(options.printHouseId);
    pendingRefreshOptions = {
      ...pendingRefreshOptions,
      ...options,
      printHouseId: options.printHouseId,
    };
  } else if (options?.timeZoneOffset != null) {
    pendingRefreshOptions = { ...pendingRefreshOptions, ...options };
  }

  // If already refreshing, queue this request
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  const effectiveOptions = pendingRefreshOptions ?? options;
  pendingRefreshOptions = undefined;

  try {
    const accessToken = getUserToken();
    const { printHouseId, timeZoneOffset } =
      resolveRefreshPrintHouseContext(effectiveOptions);

    if (printHouseId) {
      setSessionPrintHouseId(printHouseId);
    }

    const result = await apiRequest("POST", "/v1/auth/refresh-token", {
      refreshToken: refreshToken,
      ...(accessToken ? { accessToken } : {}),
      ...(printHouseId ? { printHouseId } : {}),
      ...(timeZoneOffset != null ? { timeZoneOffset } : {}),
    });

    if (!result.success) {
      throw new Error(result.message || "Failed to refresh token");
    }

    // Handle response format: result.data.data.token or result.data.token
    let newAccessToken: string | null = null;
    let newRefreshToken: string | null = null;

    if (result.data?.data?.token) {
      newAccessToken = result.data.data.token;
      newRefreshToken = result.data.data.refreshToken;
    } else if (result.data?.token) {
      newAccessToken = result.data.token;
      newRefreshToken = result.data.refreshToken;
    }

    if (newAccessToken) {
      // Update tokens in storage
      updateTokenStorage(newAccessToken);
      if (newRefreshToken) {
        updateRefreshTokenStorage(newRefreshToken);
      }

      processQueue(null, newAccessToken);
      isRefreshing = false;
      return newAccessToken;
    } else {
      throw new Error("Invalid refresh token response");
    }
  } catch (error: any) {
    // Clear tokens on refresh failure
    removeItem("auth-token");
    removeRefreshTokenStorage();
    processQueue(error, null);
    isRefreshing = false;
    throw error;
  }
};
